import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ReportDrawerProps {
  open: boolean;
  onClose: () => void;
  markdown: string | null;
  loading: boolean;
}

const ReportDrawer: React.FC<ReportDrawerProps> = ({
  open,
  onClose,
  markdown,
  loading,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: { xs: '100%', md: 700 } } }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        borderBottom={1}
        borderColor="divider"
      >
        <Typography variant="h6">Analysis Report</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box px={3} py={2} overflow="auto" flex={1}>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !markdown && (
          <Typography color="text.secondary">
            No report available for this run.
          </Typography>
        )}

        {!loading && markdown && (
          <Box
            sx={{
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                mb: 2,
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'action.hover',
                fontWeight: 600,
              },
              '& img': { maxWidth: '100%' },
              '& pre': {
                bgcolor: 'action.hover',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
              },
              '& code': {
                fontSize: '0.85em',
              },
              '& h1, & h2, & h3': { mt: 3, mb: 1 },
              '& p': { mb: 1.5 },
            }}
          >
            <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ReportDrawer;
